import { Editor } from '@monaco-editor/react';
import { Moon, Sun } from 'lucide-react';
import Box from 'images/menu_icon/product_menu_blue.svg';
import { isArray } from 'lodash';
import * as React from 'react';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { getFormTemplates, getLastStartedAtFormInstance } from '../../atoms/forms';
import Dropdown from '../../components_v2/dropdown/Dropdown';
import { DropdownData } from '../../components_v2/dropdown/model/Model';
import Input from '../../components_v2/input/Input';
import { ButtonStyle } from '../../components_v2/popup/PopupCreation';
import { PanelSelector } from '../../components_v2/Selector/PanelSelector';
import { getUrlParameterByName } from '../../components_v2/utils';
import { getLocalStorage } from '../../helpers/localStorage';
import { DefaultButton } from '../../styles/global/css/GlobalButton';
import { PrimaryColor, DarkGreySidely2, SidelyBlack, SurfaceColor } from '../../styles/global/css/Utils';
import { Translate, translateToString } from '../../styles/global/translate';
import { useKeyPress } from '../../utils/customHooks';
import bonDeCommande from './email-templates/bon-de-commande.html';
import { ToolbarBox } from '../globals/defaultToolbar/style/Style';
import { ComponentProps } from '../globals/mainPage/mainPage';
import PageSwitch from '../map/PageSwitch';
import { FlexDiv } from '../products/style';
import { FormEmailRecipient, getFormEmailById, postFormEmail, putFormEmail } from '../settings/subSettings/actions';
import { getAllFormViews, View } from './actions';
import { FormTemplate } from './jsonValidator';
import { TemplateRenderer } from './templateRenderer';

type EditorColor = 'light' | 'vs-dark';

const StyledLabel = styled.div`
	font-size: 12px;
	color: ${DarkGreySidely2};
	text-wrap: nowrap;
`;

const TemplatesContainer = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, 180px);
	height: 100%;
	overflow-y: auto;
	background-color: ${SurfaceColor};
	gap: 16px;
	padding: 16px;
`;

const TemplateItem = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	gap: 12px;
	font-size: 12px;
	padding: 8px;
	height: 220px;
	width: 180px;
	border-radius: 5px;
	font-size: 13px;
	box-shadow: 0px 0px 5px 1px color-mix(in oklab, ${SidelyBlack} 8%, transparent);
`;

const TemplateLogo = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	width: 36px;
	aspect-ratio: 1;
	background-color: color-mix(in oklab, ${PrimaryColor} 20%, transparent);
	border-radius: 20px;
	img {
		width: 24px;
	}
`;

type EditableFormEmail = {
	id?: number,
	form_id?: number,
	view_id?: number,
	emails: FormEmailRecipient[],
	title: string,
	message: string,
}

function debounce<T extends(...args: any[]) => void>(func: T, delay: number): (...args: Parameters<T>) => void {
	let timeoutId: NodeJS.Timeout | null = null;

	return (...args: Parameters<T>) => {
		if (timeoutId) {
			clearTimeout(timeoutId);
		}
		timeoutId = setTimeout(() => {
			func(...args);
		}, delay);
	};
}

function recipientToDropdownData(recipient: FormEmailRecipient): DropdownData {
	if (typeof recipient === 'string') {
		if (recipient === 'CompanyEmail') return { label: translateToString('company_email'), value: 'CompanyEmail', checked: true };
		if (recipient === 'UserEmail') return { label: translateToString('user_email'), value: 'UserEmail', checked: true };
		return { label: recipient, value: recipient, checked: true };
	}
	if ('Email' in recipient) return { label: recipient.Email, value: `EMAIL:${recipient.Email}`, checked: true };
	return { label: `${translateToString('additional_columns')} ${recipient.AdditionalColumn}`, value: `AC:${recipient.AdditionalColumn}`, checked: true };
}

function dropdownValueToRecipient(value: string): FormEmailRecipient {
	if (value === 'CompanyEmail' || value === 'UserEmail') return value;
	if (value.startsWith('EMAIL:')) return { Email: value.slice('EMAIL:'.length) };
	return { AdditionalColumn: parseInt(value.slice('AC:'.length)) };
}

const NO_RECIPIENT = 'NoRecipient';

export default function EmailV2TemplateCreator(props: ComponentProps) {
	const history = useHistory();
	const emailId = parseInt(getUrlParameterByName('id') ?? 'NaN');
	const [formEmail, setFormEmail] = React.useState<EditableFormEmail | undefined>(undefined);
	const [templates, setTemplates] = React.useState<FormTemplate[]>([]);
	const [formViews, setFormViews] = React.useState<View[]>([]);
	const [editorColorMode, setEditorColorMode] = React.useState<EditorColor>(getLocalStorage('isDarkMode') ? 'vs-dark' : 'light');
	const [lastFormInstanceUuid, setLastFormInstanceUuid] = React.useState<string | undefined>(undefined);
	const [emailDraft, setEmailDraft] = React.useState<string>('');
	const [hideTemplate, setHideTemplate] = React.useState(false);
	const [panels, setPanels] = React.useState<{ label: string }[]>([{ label: 'Templates' }]);
	const [activePanel, setActivePanel] = React.useState<string>(panels[0].label);
	const enableSave = formEmail !== undefined && formEmail.form_id !== undefined && formEmail.view_id !== undefined;
	const emailTemplates = [
		{
			label: 'Bon de commande',
			content: bonDeCommande,
			icon: Box,
			defaultTitle: 'Bon de commande: {{ form_instance.made_at | date(format="%d-%m-%Y", timezone="Europe/Paris") }} - {{ company.company_name }}'
		}
	];

	React.useEffect(() => {
		const basePanels = [
			{ label: translateToString('email_template_subject') },
			{ label: translateToString('email_template_body') },
			{ label: translateToString('vizualization') }
		];
		setPanels(!formEmail?.id && !hideTemplate ? [{ label: 'Templates' }, ...basePanels] : basePanels);
		setActivePanel(!formEmail?.id && !hideTemplate ? 'Templates' : translateToString('email_template_subject'));
	}, [formEmail?.id, hideTemplate]);

	const debouncedSetTitle = React.useMemo(() => debounce((value: string) => {
		setFormEmail(prev => prev ? { ...prev, title: value } : prev);
	}, 500), []);

	const debouncedSetMessage = React.useMemo(() => debounce((value: string) => {
		setFormEmail(prev => prev ? { ...prev, message: value } : prev);
	}, 500), []);

	React.useEffect(() => {
		if (!isNaN(emailId)) {
			getFormEmailById(emailId).then(res => setFormEmail({
				id: res.id,
				form_id: res.form_id,
				view_id: res.view_id,
				emails: res.emails,
				title: res.title,
				message: res.message
			}));
		} else {
			setFormEmail({ id: undefined, form_id: undefined, view_id: undefined, emails: [], title: '', message: '' });
		}
		getFormTemplates().then((res) => setTemplates(res.allForms));
		getAllFormViews().then(setFormViews);
	}, []);

	React.useEffect(() => {
		if (formEmail?.form_id)
			getLastStartedAtFormInstance(formEmail.form_id).then(res => { if (res) setLastFormInstanceUuid(res.uuid); });
	}, [formEmail?.form_id]);

	const saveFormEmail = React.useCallback((quit: boolean) => async() => {
		if (!formEmail || formEmail.form_id === undefined || formEmail.view_id === undefined) return;
		if (formEmail.id !== undefined) {
			await putFormEmail({
				id: formEmail.id,
				form_id: formEmail.form_id,
				view_id: formEmail.view_id,
				emails: formEmail.emails,
				title: formEmail.title,
				message: formEmail.message
			});
		} else {
			const newId = await postFormEmail({
				form_id: formEmail.form_id,
				view_id: formEmail.view_id,
				emails: formEmail.emails,
				title: formEmail.title,
				message: formEmail.message
			});
			setFormEmail(prev => prev ? { ...prev, id: newId } : prev);
		}
		if (quit)
			history.push({ pathname: 'settings', hash: 'emails_v2_list' });
	}, [formEmail]);

	useKeyPress([{ key: 's', metaKey: true }], () => {
		if (enableSave)
			saveFormEmail(false)();
	});

	const formTemplateList: DropdownData[] = React.useMemo(
		() => templates.map((template) => ({ label: template.name, value: template.id })),
		[templates]);

	const formViewList: DropdownData[] = React.useMemo(
		() => formViews.map((view) => ({ label: view.name, value: view.id })),
		[formViews]);

	const recipientList: DropdownData[] = React.useMemo(() => {
		const emails = formEmail?.emails ?? [];
		const fixed: DropdownData[] = [
			{ label: translateToString('no_recipient'), value: NO_RECIPIENT, checked: emails.length === 0 },
			{ label: translateToString('company_email'), value: 'CompanyEmail', checked: emails.includes('CompanyEmail') },
			{ label: translateToString('user_email'), value: 'UserEmail', checked: emails.includes('UserEmail') }
		];
		const others = emails.filter(e => typeof e !== 'string').map(recipientToDropdownData);
		return [...fixed, ...others];
	}, [formEmail?.emails]);

	const addEmailDraft = React.useCallback(() => {
		const email = emailDraft.trim();
		if (!email || !email.includes('@')) return;
		setFormEmail(prev => {
			if (!prev) return prev;
			if (prev.emails.some(e => typeof e !== 'string' && 'Email' in e && e.Email === email)) return prev;
			return { ...prev, emails: [...prev.emails, { Email: email }] };
		});
		setEmailDraft('');
	}, [emailDraft]);

	const useTemplate = React.useCallback((template: { label: string, content: string, defaultTitle: string }) => {
		setFormEmail(prev => prev ? {
			...prev,
			message: template.content,
			title: prev.title.length ? prev.title : template.defaultTitle
		} : prev);
		setHideTemplate(true);
	}, []);

	React.useEffect(() => {
		if (formEmail) {
			props.setToolBarState({
				title: 'Emails v2',
				bottomLeftToolbarComponent: <ToolbarBox>
					<FlexDiv gap={'4px'}>
						<StyledLabel><Translate id='linked_form' />:</StyledLabel>
						<Dropdown
							name='dropdown_email_form'
							datalist={formTemplateList}
							selectedValue={formTemplateList.find(t => t.value === formEmail.form_id)}
							onChange={(value) => {
								if (isArray(value)) return;
								setFormEmail({ ...formEmail, form_id: value.value });
							}}
							dropdownStyle={{
								width: '200px',
								optionHeight: '300px'
							}}
						/>
					</FlexDiv>
					<FlexDiv gap={'4px'}>
						<StyledLabel><Translate id='linked_form_template_view' />:</StyledLabel>
						<Dropdown
							name='dropdown_email_view'
							datalist={formViewList}
							selectedValue={formViewList.find(v => v.value === formEmail.view_id)}
							onChange={(value) => {
								if (isArray(value)) return;
								setFormEmail({ ...formEmail, view_id: value.value });
							}}
							dropdownStyle={{
								width: '200px',
								optionHeight: '300px'
							}}
						/>
					</FlexDiv>
					<FlexDiv gap={'4px'}>
						<StyledLabel><Translate id='recipient' />:</StyledLabel>
						<Dropdown
							name='dropdown_email_recipients'
							datalist={recipientList}
							onChange={(value) => {
								if (!isArray(value)) return;
								// "No recipient" was just checked (it wasn't implied by an already-empty list): clear everything else.
								const wasNoRecipientImplied = formEmail.emails.length === 0;
								const noRecipientNowChecked = value.some(v => v.value === NO_RECIPIENT);
								if (noRecipientNowChecked && !wasNoRecipientImplied) {
									setFormEmail({ ...formEmail, emails: [] });
									return;
								}
								setFormEmail({ ...formEmail, emails: value.filter(v => v.value !== NO_RECIPIENT).map((v) => dropdownValueToRecipient(v.value)) });
							}}
							isMulti
							checkbox
							doNotDeleteOptionOnSelect
							dropdownStyle={{
								width: '220px',
								optionHeight: '300px'
							}}
						/>
					</FlexDiv>
					<FlexDiv gap={'4px'}>
						<Input
							name='input_email_recipient'
							type='text'
							value={emailDraft}
							placeholder={translateToString('email')}
							onChange={(value: string) => setEmailDraft(value)}
							inputStyle={{
								width: '180px',
								borderRadius: '5px'
							}}
						/>
						<DefaultButton
							margin='0px'
							buttonStyle={ButtonStyle.White}
							onClick={addEmailDraft}
						>
							<Translate id='add' />
						</DefaultButton>
					</FlexDiv>
					<PageSwitch<EditorColor>
						background={true}
						selectedValue={editorColorMode}
						list={[
							{ value: 'light', img: <Sun />, onClick: (value) => { setEditorColorMode(value); localStorage.setItem('isDarkMode', JSON.stringify(value === 'vs-dark')); } },
							{ value: 'vs-dark', img: <Moon />, onClick: (value) => { setEditorColorMode(value); localStorage.setItem('isDarkMode', JSON.stringify(value === 'vs-dark')); } }
						]} />
				</ToolbarBox>,
				bottomRightToolbarComponent: <FlexDiv gap='8px'>
					<DefaultButton
						onClick={() => history.push({ pathname: 'settings', hash: 'emails_v2_list' })}
						buttonStyle={ButtonStyle.White}
						margin='0px'
					>
						<Translate id='back' />
					</DefaultButton>
					<DefaultButton
						onClick={saveFormEmail(false)}
						margin='0px'
						disabled={!enableSave}
					>
						<Translate id='save' />
					</DefaultButton>
					<DefaultButton
						onClick={saveFormEmail(true)}
						margin='0px'
						disabled={!enableSave}
					>
						<Translate id='save_and_quit' />
					</DefaultButton>
				</FlexDiv>
			});
		}
	}, [formEmail, editorColorMode, enableSave, templates, formViews, emailDraft]);

	return <div style={{ height: 'calc(100vh - 150px)' }}>
		<PanelSelector
			borderBottom
			panels={panels}
			activeIndex={panels.findIndex((panel) => panel.label === activePanel)}
			onClick={(panel) => {
				setActivePanel(panel.label);
			}}
		/>
		{activePanel === 'Templates' && !hideTemplate &&
			<TemplatesContainer>
				{emailTemplates.map((template, index) => {
					return <TemplateItem key={`${template.label}${index}`}>
						<TemplateLogo><img src={template.icon} /></TemplateLogo>
						<span style={{ textAlign: 'center', height: '30px' }}>{template.label}</span>
						<DefaultButton onClick={() => useTemplate(template)}>
							<Translate id='form_view_templates.use_template' />
						</DefaultButton>
					</TemplateItem>;
				})}
			</TemplatesContainer>
		}
		{formEmail &&
			<div style={{ height: '100%', display: activePanel === translateToString('email_template_subject') ? 'block' : 'none' }}>
				<Editor
					height='100%'
					value={formEmail.title}
					language='html'
					onChange={(value) => {
						if (value !== undefined)
							debouncedSetTitle(value);
					}}
					theme={editorColorMode}
				/>
			</div>
		}
		{formEmail &&
			<div style={{ height: '100%', display: activePanel === translateToString('email_template_body') ? 'block' : 'none' }}>
				<Editor
					height='100%'
					value={formEmail.message}
					language='html'
					onChange={(value) => {
						if (value !== undefined)
							debouncedSetMessage(value);
					}}
					theme={editorColorMode}
				/>
			</div>
		}
		{formEmail && lastFormInstanceUuid && activePanel === translateToString('vizualization') &&
			<div style={{ backgroundColor: SurfaceColor, width: '100%', height: '100%' }}>
				<TemplateRenderer
					template={`<h2>${formEmail.title}</h2><hr/>${formEmail.message}`}
					form_instance_uuid={lastFormInstanceUuid}
					height={'calc(100% - 10px)'}
				/>
			</div>
		}
	</div>;
}
